// `extension-enrichment-report.api.ng1.ts` — the candidate "Report" action's one call, which
// files a recruiter's complaint about a candidate's enrichment (wrong LinkedIn profile, stale
// work history, …) for the enrichment team to review.
//
// The reason enum and the payload are declared upstream in
// `shortlist.report-enrichment.service.ng1.ts`, not in the API file; they live here because this
// is where the wire contract is, and `packages/ui` builds the payload against it.
import { request } from './request.ts'

/** `EnrichmentReportReason`. String values, on the wire. */
export const EnrichmentReportReason = {
  IncorrectLinkedinProfile: 'INCORRECT_LINKEDIN_PROFILE',
  OutdatedWorkHistory: 'OUTDATED_WORK_HISTORY',
  LinkedinProfileUnreachable: 'LINKEDIN_PROFILE_UNREACHABLE',
  LocationMismatch: 'LOCATION_MISMATCH',
  DataEntirelyMissing: 'DATA_ENTIRELY_MISSING',
  Other: 'OTHER',
} as const
export type EnrichmentReportReason = (typeof EnrichmentReportReason)[keyof typeof EnrichmentReportReason]

/**
 * `EnrichmentReportPayload`.
 *
 * ⚠️ `alertId` IS `null`, NOT ABSENT, when there is no alert: the snippet's trigger passes
 * `!!this.alert ? this.alert.id : null`, and `$http` serialises that as `"alertId": null`.
 * `linkedinUrl` and `notes` are absent until the user types in them, and `""` once they have
 * typed and cleared — both are what upstream sends.
 */
export interface EnrichmentReportPayload {
  candidateId: number
  alertId?: number | null
  reasons: EnrichmentReportReason[]
  linkedinUrl?: string
  notes?: string
}

/**
 * `saveEnrichmentReport` — POST, the payload as the JSON body, no response body. ⚠️ A WRITE: the
 * report is stored on the tenant against the candidate.
 */
export async function saveEnrichmentReport(payload: EnrichmentReportPayload): Promise<void> {
  await request<unknown>('POST', '/chrome-extension-api/enrichment-report/saveEnrichmentReport', payload)
}
